/* POSHRADS — cart-drawer.js: Cart Ajax API + Section Rendering ("cart-drawer"). See docs/core-templates.md §6. */
(() => {
  'use strict';

  if (window.poshCartDrawerReady) return;
  window.poshCartDrawerReady = true;

  const drawer = document.querySelector('[data-cart-drawer]');
  if (!drawer) return;
  const panel = drawer.querySelector('[data-cart-drawer-panel]');
  const live = drawer.querySelector('[data-cart-live]');
  const FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]), select, textarea';
  let launcher = null;
  let inerted = [];
  let quantityTimer = null;
  let busy = false;
  const queue = new Map(); // line key -> latest requested quantity

  const isOpen = () => drawer.classList.contains('is-open');

  const setInert = (on) => {
    if (!on) {
      inerted.forEach((el) => (el.inert = false));
      inerted = [];
      return;
    }
    let node = drawer;
    while (node && node !== document.body) {
      for (const sibling of node.parentElement.children) {
        if (sibling !== node && !sibling.inert) {
          sibling.inert = true;
          inerted.push(sibling);
        }
      }
      node = node.parentElement;
    }
  };

  const open = (from) => {
    launcher = from || document.activeElement;
    if (!isOpen()) {
      drawer.classList.add('is-open');
      panel.setAttribute('role', 'dialog');
      panel.setAttribute('aria-modal', 'true');
      panel.setAttribute('aria-labelledby', panel.dataset.labelledby);
      document.documentElement.classList.add('scroll-lock');
      setInert(true);
    }
    const close = panel.querySelector('button[data-cart-drawer-close]');
    (close || panel).focus();
  };

  const close = () => {
    if (!isOpen()) return;
    drawer.classList.remove('is-open');
    panel.removeAttribute('role');
    panel.removeAttribute('aria-modal');
    panel.removeAttribute('aria-labelledby');
    document.documentElement.classList.remove('scroll-lock');
    setInert(false);
    if (launcher && document.contains(launcher)) launcher.focus();
    launcher = null;
  };

  const announce = (text) => {
    live.textContent = '';
    window.setTimeout(() => (live.textContent = text), 50);
  };

  const render = (html) => {
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const fresh = doc.querySelector('[data-cart-drawer-body]');
    const current = drawer.querySelector('[data-cart-drawer-body]');
    if (!fresh || !current) return false;
    const focusedId = document.activeElement && current.contains(document.activeElement) ? document.activeElement.id : null;
    current.replaceWith(fresh);

    const state = fresh.querySelector('[data-cart-header-state]');
    if (state) {
      document.querySelectorAll('[data-cart-count]').forEach((badge) => {
        badge.setAttribute('data-cart-count', state.dataset.count);
        badge.textContent = state.dataset.count;
      });
      document.querySelectorAll('[data-cart-label]').forEach((label) => {
        label.textContent = state.content.textContent.trim();
      });
    }

    if (isOpen()) {
      const target = focusedId && document.getElementById(focusedId);
      (target || panel.querySelector('button[data-cart-drawer-close]') || panel).focus();
    }
    return true;
  };

  // Bundled section HTML, else a separate section render, else the cart page (never a stale cart).
  const refresh = async (sections) => {
    const html = sections && sections['cart-drawer'];
    if (html && render(html)) return;
    try {
      const response = await fetch(`${window.location.pathname}?sections=cart-drawer`, { headers: { Accept: 'application/json' } });
      const json = response.ok ? await response.json() : null;
      if (json && json['cart-drawer'] && render(json['cart-drawer'])) return;
    } catch (error) {
      // fall through to the full cart page
    }
    window.location.href = drawer.dataset.cartUrl;
  };

  // Only Shopify's own messages reach shoppers; network/parse failures use the translated text.
  const shopifyError = (data) => Object.assign(new Error(data.description || data.message || ''), { shopify: true });
  const message = (error) => (error.shopify && error.message) || drawer.dataset.errorText;
  const readJson = async (response) => {
    try {
      return await response.json();
    } catch (error) {
      return { status: response.status, description: drawer.dataset.errorText };
    }
  };

  const request = async (url, body) => {
    const response = await fetch(`${url}.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ ...body, sections: ['cart-drawer'], sections_url: window.location.pathname }),
    });
    const data = await readJson(response);
    if (!response.ok || data.status) throw shopifyError(data);
    return data;
  };

  const processQueue = async () => {
    busy = true;
    drawer.setAttribute('aria-busy', 'true');
    while (queue.size) {
      const [key, quantity] = queue.entries().next().value;
      queue.delete(key);
      try {
        const data = await request(drawer.dataset.cartChangeUrl, { id: key, quantity });
        await refresh(data.sections);
        announce(drawer.dataset.updatedText);
      } catch (error) {
        announce(message(error));
        await refresh(null); // re-sync inputs with the real cart after a rejected change
      }
    }
    busy = false;
    drawer.removeAttribute('aria-busy');
  };

  const changeLine = (key, quantity) => {
    if (!key) return;
    queue.set(key, quantity);
    if (!busy) processQueue();
  };

  // Quantity rules (min / increment / max) from the input.
  const snap = (input, value) => {
    const min = Number(input.min) || 1;
    const step = Number(input.step) || 1;
    const max = input.max ? Number(input.max) : Infinity;
    return Math.min(max, Math.max(min, Math.round(value / step) * step));
  };

  const lineKey = (el) => el.closest('[data-line-key]')?.dataset.lineKey;

  document.addEventListener('click', (event) => {
    const trigger = event.target.closest('[data-cart-trigger]');
    if (trigger) {
      event.preventDefault();
      open(trigger);
      return;
    }
    if (!drawer.contains(event.target)) return;

    if (event.target.closest('[data-cart-drawer-close]')) {
      close();
      return;
    }
    const step = event.target.closest('[data-cart-step]');
    if (step) {
      const input = document.getElementById(step.getAttribute('aria-controls'));
      const current = Number(input.value || 0);
      const next = snap(input, current + Number(step.dataset.cartStep) * (Number(input.step) || 1));
      if (next === current) return; // at the rule minimum/maximum; removal is the explicit Remove button
      input.value = next;
      changeLine(lineKey(step), next);
      return;
    }
    const remove = event.target.closest('[data-cart-remove]');
    if (remove) changeLine(lineKey(remove), 0);
  });

  document.addEventListener('input', (event) => {
    const input = event.target.closest('[data-cart-quantity]');
    if (!input || !drawer.contains(input)) return;
    clearTimeout(quantityTimer);
    quantityTimer = window.setTimeout(() => {
      const value = Number.parseInt(input.value, 10);
      if (!Number.isFinite(value)) return;
      const next = snap(input, value);
      input.value = next;
      changeLine(lineKey(input), next);
    }, 500);
  });

  document.addEventListener('keydown', (event) => {
    if (!isOpen()) return;
    if (event.key === 'Escape') {
      event.preventDefault();
      close();
      return;
    }
    if (event.key !== 'Tab') return;
    const items = [...panel.querySelectorAll(FOCUSABLE)].filter((el) => el.getClientRects().length);
    if (!items.length) return;
    const first = items[0];
    const last = items[items.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  });

  // Add to cart from product forms.
  document.addEventListener('submit', async (event) => {
    const form = event.target.closest('form.product-form');
    // Only name="add" is enhanced; other submitters stay native.
    if (!form || (event.submitter && event.submitter.name !== 'add')) return;
    event.preventDefault();
    const submitter = event.submitter || form.querySelector('[name="add"]');
    const error = form.closest('.product__buy')?.querySelector('[data-product-form-error]');
    if (error) {
      error.hidden = true;
      error.textContent = '';
    }
    if (submitter && submitter.getAttribute('aria-busy') === 'true') return; // no double add
    if (submitter) submitter.setAttribute('aria-busy', 'true');

    const formData = new FormData(form);
    formData.append('sections', 'cart-drawer');
    formData.append('sections_url', window.location.pathname);

    try {
      const response = await fetch(`${drawer.dataset.cartAddUrl}.js`, {
        method: 'POST',
        headers: { Accept: 'application/json' },
        body: formData,
      });
      const data = await readJson(response);
      if (!response.ok || data.status) throw shopifyError(data);
      await refresh(data.sections);
      open(submitter);
      announce(drawer.dataset.addedText);
    } catch (err) {
      // Shopify errors in the form; other failures: native post.
      if (err.shopify && error) {
        error.textContent = message(err);
        error.hidden = false;
      } else {
        form.submit();
      }
    } finally {
      if (submitter) submitter.removeAttribute('aria-busy');
    }
  });

  document.addEventListener('shopify:section:unload', (event) => {
    if (event.target.contains(drawer)) close();
  });
})();
